import { useEffect } from "react";

/**
 * Lightweight SEO hook — updates document.title + head meta tags on route
 * mount so Googlebot's JS renderer picks up per-page metadata without
 * pulling in react-helmet.
 *
 * Usage:
 *   useSeo({ title: "Live Trading Charts · Tradelog", description: "...", canonical: "/charts" });
 */
const SITE = "https://thetradlog.com";
const DEFAULT_OG_IMAGE = "https://thetradlog.com/img/og-cover.png";

function setMeta(attr, key, content) {
  if (content == null) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (href == null) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  // Replace any previous script we injected with the same id so route changes
  // don't leave stale schema behind.
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.id = id;
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}

export function useSeo({ title, description, canonical, ogImage, noindex, jsonLd } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    const abs = canonical
      ? (canonical.startsWith("http") ? canonical : SITE + canonical)
      : null;
    if (description) {
      setMeta("name", "description", description);
      setMeta("property", "og:description", description);
      setMeta("name", "twitter:description", description);
    }
    if (title) {
      setMeta("property", "og:title", title);
      setMeta("name", "twitter:title", title);
    }
    if (abs) {
      setLink("canonical", abs);
      setMeta("property", "og:url", abs);
    }
    const img = ogImage || DEFAULT_OG_IMAGE;
    setMeta("property", "og:image", img);
    setMeta("name", "twitter:image", img);
    if (noindex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      setMeta("name", "robots", "index, follow, max-image-preview:large");
    }
    // Per-page structured data — schema.org JSON-LD. Accepts a single object
    // or an array of objects (e.g. Article + BreadcrumbList).
    if (jsonLd) {
      const arr = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      arr.forEach((obj, i) => setJsonLd(`tj-jsonld-${i}`, obj));
    } else {
      // Clear any previously injected schema on routes that don't set one.
      for (let i = 0; i < 4; i++) setJsonLd(`tj-jsonld-${i}`, null);
    }
    return () => { if (title) document.title = prevTitle; };
  }, [title, description, canonical, ogImage, noindex, jsonLd]);
}

export default useSeo;
